import ReactPixel from "react-facebook-pixel";

const META_PIXEL_ID =
  process.env.REACT_APP_META_PIXEL_ID || "1010371221889389";

const currency = "USD";
let isInitialized = false;

type EventProperties = Record<string, unknown>;
type StartFunnelCustomEvent =
  | "StartFunnelStarted"
  | "StartFunnelQuizStarted"
  | "StartFunnelQuizAnswered"
  | "StartFunnelQuizCompleted"
  | "StartFunnelEmailSubmitted"
  | "StartFunnelPackUnlockClicked"
  | "StartFunnelPaywallViewed"
  | "StartFunnelCheckoutStarted"
  | "StartFunnelCheckoutCreated"
  | "StartFunnelCheckoutFailed"
  | "StartFunnelPurchaseCompleted"
  | "StartFunnelTrialStarted"
  | "StartFunnelAuthCompleted";

const toDollars = (amount?: number | string | null, fallback = 0) => {
  if (typeof amount === "number") {
    return amount > 100 ? amount / 100 : amount;
  }

  if (typeof amount === "string") {
    const parsed = Number(amount);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  return fallback;
};

const sendStandardEvent = (event: string, properties?: EventProperties) => {
  if (!isInitialized) {
    initMetaPixel();
  }

  ReactPixel.track(event, properties || {});
};

const sendCustomEvent = (event: string, properties?: EventProperties) => {
  if (!isInitialized) {
    initMetaPixel();
  }

  ReactPixel.trackCustom(event, properties || {});
};

export const initMetaPixel = () => {
  if (isInitialized) {
    return;
  }

  ReactPixel.init(META_PIXEL_ID);
  isInitialized = true;
};

export const trackPageView = () => {
  if (!isInitialized) {
    initMetaPixel();
  }

  ReactPixel.pageView();
};

export const trackLead = (properties?: EventProperties) => {
  sendStandardEvent("Lead", {
    content_name: "Sign in",
    ...properties,
  });
};

export const trackToolView = ({
  title,
  category,
  featured,
}: {
  title?: string;
  category: string;
  featured?: boolean;
}) => {
  sendStandardEvent("ViewContent", {
    content_name: title,
    content_category: category,
    content_type: "product",
    featured: Boolean(featured),
  });
};

export const trackPaywallView = (properties?: EventProperties) => {
  sendCustomEvent("PaywallView", properties);
};

export const trackCheckoutStart = ({
  plan,
  amount,
}: {
  plan: string;
  amount?: number;
}) => {
  sendStandardEvent("InitiateCheckout", {
    content_name: plan,
    content_category: "subscription",
    content_type: "product",
    currency,
    num_items: 1,
    value: toDollars(amount),
  });
};

export const trackTrialCheckoutStart = (amount?: number) => {
  sendStandardEvent("InitiateCheckout", {
    content_name: "trial",
    content_category: "subscription",
    content_type: "product",
    currency,
    num_items: 1,
    value: toDollars(amount, 29.99),
  });
};

export const trackStartTrial = (amount?: number | string | null) => {
  sendStandardEvent("StartTrial", {
    currency,
    value: toDollars(amount, 29.99),
  });
};

export const trackSubscriptionPurchase = (
  amount?: number | string | null,
  plan = "subscription"
) => {
  sendStandardEvent("Purchase", {
    content_name: plan,
    content_category: "subscription",
    content_type: "product",
    currency,
    num_items: 1,
    value: toDollars(amount, 29.99),
  });

  sendStandardEvent("Subscribe", {
    currency,
    value: toDollars(amount, 29.99),
  });
};

export const trackDownload = (properties?: EventProperties) => {
  sendCustomEvent("Download", properties);
};

// --- Web2app quiz funnel (/start) events ---

export const trackStartFunnelCustomEvent = (
  event: StartFunnelCustomEvent,
  properties?: EventProperties
) => {
  sendCustomEvent(event, {
    funnel: "start_brush_quiz",
    ...properties,
  });
};

export const trackFunnelStart = (properties?: EventProperties) => {
  sendStandardEvent("ViewContent", {
    content_name: "brush_quiz_funnel",
    content_category: "funnel",
    content_type: "product",
  });

  trackStartFunnelCustomEvent("StartFunnelStarted", properties);
};

export const trackFunnelLead = (properties?: EventProperties) => {
  sendStandardEvent("Lead", {
    content_name: "brush_quiz_funnel_email",
    content_category: "funnel",
  });

  trackStartFunnelCustomEvent("StartFunnelEmailSubmitted", properties);
};

export const trackFunnelUnlock = (properties?: EventProperties) => {
  sendStandardEvent("InitiateCheckout", {
    content_name: "brush_quiz_funnel_unlock",
    content_category: "subscription",
    content_type: "product",
    currency,
    num_items: 1,
  });

  trackStartFunnelCustomEvent("StartFunnelPackUnlockClicked", properties);
};
